import toast from 'react-hot-toast';
import type { RecordModel } from 'pocketbase';
import pb from '@libs/pocketbase';
import http from '@libs/http';
import { addMonths, generateOrderCode } from './utils';
import type {
  CustomerAccountEmailPayload,
  CustomerOption,
  OrderDetailRecord,
  OrderFilters,
  OrderFormValues,
  OrderItemInput,
  OrderRecord,
  OrderStatus,
  PackageOption,
} from './types';

const ordersCollection = () => pb.collection('orders');
const orderDetailsCollection = () => pb.collection('order_details');
const customersCollection = () => pb.collection('customers');
const packagesCollection = () => pb.collection('packages');
const subscriptionsCollection = () => pb.collection('subscriptions');
const transactionsCollection = () => pb.collection('transactions');
const packageFunctionsCollection = () => pb.collection('package_functions');
const functionsCollection = () => pb.collection('functions');
const customerFunctionsCollection = () => pb.collection('customer_functions');
const MAIL_ENDPOINT = '/api/mail/customer-account';

interface CustomerRecordModel extends RecordModel {
  name: string;
  representative_name?: string;
  representative_phone?: string;
  email?: string;
  phone?: string;
  address?: string;
  tax_code?: string;
  customer_type_id?: string;
  source?: string;
  status?: 'active' | 'inactive';
  subscriptions_count?: number;
}

interface OrderRecordModel extends RecordModel {
  code: string;
  customers_id?: string;
  total_amount?: number;
  status?: OrderStatus;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  approved_at?: string;
  expand?: {
    customers_id?: CustomerRecordModel;
  };
}

interface OrderDetailModel extends RecordModel {
  order_id: string;
  package_id: string;
  quantity: number;
  price: number;
  total_price: number;
}

interface PackageRecordModel extends RecordModel {
  name: string;
  code: string;
  sale_price: number;
  status?: 'active' | 'inactive';
}

const mapCustomerRecord = (record?: CustomerRecordModel | null): CustomerOption | undefined => {
  if (!record) return undefined;
  return {
    id: record.id,
    name: record.name,
    email: record.email ?? undefined,
    representative_name: record.representative_name ?? undefined,
    status: (record.status ?? 'active') as 'active' | 'inactive',
    subscriptions_count: record.subscriptions_count,
  };
};

const mapOrderDetail = (detail: OrderDetailModel, pkg?: PackageRecordModel): OrderDetailRecord => ({
  id: detail.id,
  order_id: detail.order_id,
  package_id: detail.package_id,
  package_name: pkg?.name,
  package_code: pkg?.code,
  quantity: Number(detail.quantity ?? 0),
  price: Number(detail.price ?? 0),
  total_price: Number(detail.total_price ?? 0),
});

const mapOrderRecord = (record: OrderRecordModel, details?: OrderDetailRecord[]): OrderRecord => ({
  id: record.id,
  code: record.code,
  customers_id: record.customers_id,
  customer: mapCustomerRecord(record.expand?.customers_id),
  total_amount: Number(record.total_amount ?? 0),
  status: (record.status ?? 'pending') as OrderStatus,
  notes: record.notes ?? undefined,
  created_at: record.created_at ?? record.created,
  updated_at: record.updated_at ?? record.updated,
  approved_at: record.approved_at,
  order_details: details,
});

const buildFilter = (filters?: OrderFilters) => {
  const clauses: string[] = [];
  if (filters?.status && filters.status !== 'all') {
    clauses.push(`status="${filters.status}"`);
  }
  if (filters?.search) {
    const keyword = filters.search.replace(/"/g, '\\"');
    clauses.push(`(code~"${keyword}"||customers_id.name~"${keyword}"||customers_id.email~"${keyword}")`);
  }
  if (filters?.date_from) {
    const start = new Date(filters.date_from);
    clauses.push(`created_at>="${start.toISOString()}"`);
  }
  if (filters?.date_to) {
    const end = new Date(filters.date_to);
    end.setHours(23, 59, 59, 999);
    clauses.push(`created_at<="${end.toISOString()}"`);
  }
  clauses.push('status!="deleted"'); // defensive clause for potential future flag
  return clauses.filter(Boolean).join('&&');
};

const fetchOrderDetails = async (orderId: string) => {
  const records = await orderDetailsCollection().getFullList<OrderDetailModel>({
    filter: `order_id="${orderId}"`,
    requestKey: `order-details-${orderId}`,
  });
  if (records.length === 0) return [];

  const packageIds = Array.from(new Set(records.map((item) => item.package_id))).filter(Boolean);
  let packageMap = new Map<string, PackageRecordModel>();
  if (packageIds.length > 0) {
    const pkgRecords = await packagesCollection().getFullList<PackageRecordModel>({
      filter: packageIds.map((id) => `id="${id}"`).join('||'),
      fields: 'id,name,code,sale_price,status',
      requestKey: `packages-${orderId}`,
    });
    packageMap = new Map(pkgRecords.map((pkg) => [pkg.id, pkg]));
  }

  return records.map((detail) => mapOrderDetail(detail, packageMap.get(detail.package_id)));
};

const resolveOrderItems = async (items: OrderItemInput[]) => {
  const sanitized = items.filter((item) => item.package_id);
  if (sanitized.length === 0) {
    throw new Error('Vui lòng chọn ít nhất 1 gói dịch vụ cho đơn hàng');
  }
  const packageIds = Array.from(new Set(sanitized.map((item) => item.package_id)));
  const packages = await packagesCollection().getFullList<PackageRecordModel>({
    filter: packageIds.map((id) => `id="${id}"`).join('||'),
    fields: 'id,name,code,sale_price,status',
    requestKey: 'order-packages-resolve',
  });
  const packageMap = new Map(packages.map((pkg) => [pkg.id, pkg]));
  const resolved = sanitized.map((item) => {
    const pkg = packageMap.get(item.package_id);
    if (!pkg) {
      throw new Error('Không tìm thấy thông tin gói dịch vụ');
    }
    const quantity = Math.max(1, Number(item.quantity ?? 1));
    const price = Number(pkg.sale_price ?? 0);
    return {
      package_id: item.package_id,
      quantity,
      price,
      total: price * quantity,
    };
  });
  const totalAmount = resolved.reduce((sum, item) => sum + item.total, 0);
  return { resolved, totalAmount };
};

// Helper function để extract error message từ PocketBase error
const extractErrorMessage = (error: unknown, defaultMessage: string): string => {
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  // Kiểm tra error.data
  if ('data' in error && error.data && typeof error.data === 'object') {
    const errorData = error.data as Record<string, unknown>;
    
    // Log toàn bộ error.data để debug
    console.error('PocketBase error data:', JSON.stringify(errorData, null, 2));
    
    // Thử lấy message từ các field phổ biến
    const fields = ['email', 'username', 'password', 'name', 'passwordConfirm'];
    for (const field of fields) {
      if (field in errorData && errorData[field]) {
        if (typeof errorData[field] === 'object' && errorData[field] !== null) {
          const fieldError = errorData[field] as Record<string, unknown>;
          if ('message' in fieldError && typeof fieldError.message === 'string') {
            return `${field}: ${fieldError.message}`;
          }
          if ('code' in fieldError && typeof fieldError.code === 'string') {
            return `${field}: ${fieldError.code}`;
          }
        } else if (typeof errorData[field] === 'string') {
          return `${field}: ${errorData[field]}`;
        }
      }
    }
    
    // Thử lấy message tổng quát từ error.data
    if ('message' in errorData && typeof errorData.message === 'string') {
      return errorData.message;
    }
  }

  // Thử lấy message trực tiếp từ error
  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return defaultMessage;
};

const ensureCustomer = async (values: OrderFormValues) => {
  if (values.orderType === 'existing') {
    if (!values.customer_id) {
      throw new Error('Vui lòng chọn khách hàng hiện có');
    }
    return values.customer_id;
  }

  if (!values.customer_name || !values.email) {
    throw new Error('Khách hàng mới cần tên công ty và email');
  }
  if (!values.account_username || !values.account_password) {
    throw new Error('Khách hàng mới cần tên đăng nhập và mật khẩu tạm thời');
  }

  const normalizedEmail = values.email.trim().toLowerCase();
  const normalizedUsername = values.account_username.trim();
  const tempPassword = values.account_password.trim();

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(normalizedEmail)) {
    throw new Error('Email không hợp lệ');
  }

  // Validate password length
  if (tempPassword.length < 8) {
    throw new Error('Mật khẩu phải có ít nhất 8 ký tự');
  }

  // Kiểm tra xem user đã tồn tại chưa (theo email hoặc username)
  let existingUser = null;
  try {
    // Kiểm tra theo email
    const usersByEmail = await pb.collection('users').getList(1, 1, {
      filter: `email="${normalizedEmail}"`,
      requestKey: `check-user-email-${Date.now()}`,
    });
    if (usersByEmail.items && usersByEmail.items.length > 0) {
      existingUser = usersByEmail.items[0];
      console.log('Found existing user by email:', existingUser.id);
    }
  } catch (error) {
    // Log nhưng không throw, có thể do permission hoặc user chưa tồn tại
    console.log('No existing user found by email, continuing...');
  }

  // Nếu chưa tìm thấy, kiểm tra theo username
  if (!existingUser && normalizedUsername) {
    try {
      const usersByUsername = await pb.collection('users').getList(1, 1, {
        filter: `username="${normalizedUsername}"`,
        requestKey: `check-user-username-${Date.now()}`,
      });
      if (usersByUsername.items && usersByUsername.items.length > 0) {
        existingUser = usersByUsername.items[0];
        console.log('Found existing user by username:', existingUser.id);
      }
    } catch (error) {
      // Log nhưng không throw, có thể do permission hoặc user chưa tồn tại
      console.log('No existing user found by username, continuing...');
    }
  }

  // Nếu user đã tồn tại, sử dụng user đó và bỏ qua việc tạo mới
  if (existingUser) {
    console.log('Using existing user:', existingUser.id);
    // Không cần tạo user mới, tiếp tục với việc tạo customer
  } else {
    // Tạo user mới
    // Lưu ý: Collection users có createRule rỗng, chỉ admin mới có quyền tạo
    // Đảm bảo pb đã được authenticate với super_admin
    
    // Kiểm tra xem có đang authenticate với super_admin không
    const authModel = pb.authStore.model;
    if (!authModel || (authModel as { collectionName?: string }).collectionName !== 'super_admins') {
      console.warn('Warning: Not authenticated as super_admin. User creation may fail.');
      console.log('Current auth model:', authModel);
    } else {
      console.log('Authenticated as super_admin, proceeding with user creation...');
    }
    
    try {
      // Tạo payload đơn giản nhất, chỉ với các field bắt buộc
      const userPayload: Record<string, unknown> = {
        email: normalizedEmail,
        password: tempPassword,
        passwordConfirm: tempPassword,
      };

      // Chỉ thêm emailVisibility nếu cần (có thể không cần thiết)
      // emailVisibility: true,

      // Chỉ thêm username nếu có
      if (normalizedUsername && normalizedUsername.trim()) {
        userPayload.username = normalizedUsername;
      }

      // Chỉ thêm name nếu có
      const userName = values.representative_name ?? values.customer_name;
      if (userName && userName.trim()) {
        userPayload.name = userName.trim();
      }

      // Log payload trước khi tạo (không log password)
      console.log('Creating user with payload:', {
        ...userPayload,
        password: '***',
        passwordConfirm: '***',
      });

      // Tạo user mới
      const createdUser = await pb.collection('users').create(userPayload);
      console.log('User created successfully:', createdUser.id);

      // Verify user sau khi tạo (nếu cần)
      // Lưu ý: Chỉ admin mới có quyền update verified field
      // Nếu update thất bại, user có thể verify sau qua email hoặc admin
      try {
        await pb.collection('users').update(createdUser.id, {
          verified: true,
        });
        console.log('User verified successfully');
      } catch (verifyError: unknown) {
        // Log warning nhưng không throw, vì user đã được tạo thành công
        console.warn('Could not verify user immediately:', {
          userId: createdUser.id,
          error: verifyError instanceof Error ? verifyError.message : 'Unknown error',
          note: 'User can verify later via email or admin',
        });
        // User có thể verify sau qua email hoặc admin
        // Không làm gián đoạn flow vì user đã được tạo thành công
      }
    } catch (error: unknown) {
      console.error('Create user error - Full error object:', error);
      
      // Log chi tiết lỗi để debug
      if (error && typeof error === 'object') {
        if ('data' in error) {
          console.error('Error data:', JSON.stringify(error.data, null, 2));
        }
        if ('message' in error) {
          console.error('Error message:', error.message);
        }
        if ('status' in error) {
          console.error('Error status:', error.status);
        }
      }

      // Xử lý lỗi cụ thể
      if (error && typeof error === 'object' && 'status' in error && error.status === 400) {
        const errorMessage = extractErrorMessage(error, 'Không thể tạo tài khoản người dùng');
        
        // Kiểm tra xem có phải lỗi validation_values_mismatch không
        if (errorMessage.toLowerCase().includes('validation_values_mismatch') || 
            errorMessage.toLowerCase().includes("values don't match")) {
          throw new Error('Lỗi validation dữ liệu. Vui lòng kiểm tra lại thông tin đã nhập (email, username, password).');
        }
        
        // Kiểm tra xem có phải lỗi permission không
        if (errorMessage.toLowerCase().includes('permission') || errorMessage.toLowerCase().includes('rule')) {
          throw new Error('Không có quyền tạo tài khoản. Vui lòng đảm bảo đã đăng nhập với tài khoản Super Admin.');
        }
        
        throw new Error(`Lỗi tạo tài khoản: ${errorMessage}`);
      }
      
      // Kiểm tra lỗi 403 (Forbidden)
      if (error && typeof error === 'object' && 'status' in error && error.status === 403) {
        throw new Error('Không có quyền tạo tài khoản. Vui lòng đảm bảo đã đăng nhập với tài khoản Super Admin.');
      }
      
      throw new Error('Không thể tạo tài khoản người dùng. Vui lòng kiểm tra lại thông tin.');
    }
  }

  // Kiểm tra xem customer đã tồn tại chưa (theo email)
  let existingCustomer = null;
  try {
    const customersByEmail = await customersCollection().getList(1, 1, {
      filter: `email="${normalizedEmail}"&&is_deleted!=true`,
      requestKey: `check-customer-email-${normalizedEmail}`,
    });
    if (customersByEmail.items.length > 0) {
      existingCustomer = customersByEmail.items[0];
    }
  } catch (error) {
    // Ignore error, continue to create new customer
  }

  let customerId: string;
  if (existingCustomer) {
    // Customer đã tồn tại, cập nhật thông tin nếu cần
    customerId = existingCustomer.id;
    // Có thể cập nhật thông tin customer ở đây nếu cần
  } else {
    // Tạo customer mới
    const payload = {
      name: values.customer_name,
      representative_name: values.representative_name,
      representative_phone: values.representative_phone,
      email: normalizedEmail,
      phone: values.phone,
      address: values.address,
      tax_code: values.tax_code,
      customer_type_id: values.customer_type_id,
      source: values.source,
      status: values.is_paid ? 'active' : 'inactive',
      notes: values.notes,
    };

    try {
      const created = await customersCollection().create(payload);
      customerId = created.id;
    } catch (error: unknown) {
      console.error('Create customer error:', error);
      // Nếu tạo customer thất bại nhưng đã tạo user, có thể cần rollback
      if (error && typeof error === 'object' && 'status' in error && error.status === 400) {
        const errorMessage = extractErrorMessage(error, 'Không thể tạo khách hàng');
        throw new Error(`Lỗi tạo khách hàng: ${errorMessage}`);
      }
      throw new Error('Không thể tạo khách hàng. Vui lòng kiểm tra lại thông tin.');
    }
  }

  // Chỉ gửi email nếu tạo user mới
  // Lưu ý: dispatchCustomerAccountEmail đã tự xử lý error, không cần try-catch
  if (!existingUser) {
    await dispatchCustomerAccountEmail(
      {
        email: normalizedEmail,
        customer_name: values.customer_name,
        username: normalizedUsername,
        password: tempPassword,
      },
      { notifySuccess: false } // Không hiển thị toast khi tạo đơn hàng
    );
  }

  return customerId;
};

const createOrderDetails = async (orderId: string, items: Array<{ package_id: string; quantity: number; price: number }>) => {
  for (const item of items) {
    // Sequential calls to avoid PocketBase auto-cancellation for concurrent creates
    // https://github.com/pocketbase/js-sdk#auto-cancellation
    // eslint-disable-next-line no-await-in-loop
    await orderDetailsCollection().create({
      order_id: orderId,
      package_id: item.package_id,
      quantity: item.quantity,
      price: item.price,
      total_price: item.price * item.quantity,
    });
  }
};

const createSubscriptionPlaceholders = async (
  customerId: string,
  orderId: string,
  items: Array<{ package_id: string; quantity: number }>
) => {
  const now = new Date();
  for (const item of items) {
    // eslint-disable-next-line no-await-in-loop
    await subscriptionsCollection().create({
      customer_id: customerId,
      package_id: item.package_id,
      order_id: orderId,
      status: 'active',
      start_date: now.toISOString(),
      end_date: addMonths(now, 12).toISOString(),
    });
  }
};

const createTransactionPlaceholder = async (orderId: string, totalAmount: number) => {
  await transactionsCollection().create({
    order_id: orderId,
    code: `TRX-${orderId}`,
    amount: totalAmount,
    currency: 'VND',
    payment_type: 'bank_transfer',
    payment_channel: 'manual',
    status: 'success',
    notes: 'Ghi nhận thanh toán thủ công từ Super Admin',
  });
};

/**
 * Snapshot functions từ packages vào customer_functions khi khách hàng thanh toán thành công
 * Logic:
 * 1. Lấy tất cả packages từ order_details
 * 2. Với mỗi package, query package_functions để lấy danh sách functions
 * 3. Với mỗi function, snapshot toàn bộ thông tin vào customer_functions
 * Mục đích: Giữ nguyên thông tin function tại thời điểm mua, không bị ảnh hưởng khi function gốc thay đổi
 */
const snapshotCustomerFunctions = async (customerId: string, packageIds: string[]) => {
  if (packageIds.length === 0) return;

  // Query package_functions để lấy danh sách function_id cho mỗi package
  const packageFunctionsFilter = packageIds.map((id) => `package_id="${id}"`).join('||');
  const packageFunctions = await packageFunctionsCollection().getFullList<{
    id: string;
    package_id: string;
    function_id: string;
  }>({
    filter: packageFunctionsFilter,
    fields: 'id,package_id,function_id',
    requestKey: `order-package-functions-${customerId}`,
  });

  if (packageFunctions.length === 0) return;

  // Lấy tất cả function IDs
  const functionIds = Array.from(new Set(packageFunctions.map((pf) => pf.function_id))).filter(Boolean);
  if (functionIds.length === 0) return;

  // Query functions để lấy thông tin đầy đủ
  const functionsFilter = functionIds.map((id) => `id="${id}"`).join('||');
  const functions = await functionsCollection().getFullList<{
    id: string;
    module_id?: string;
    route?: string;
    code: string;
    name: string;
    description?: string;
    position?: number;
    is_show?: boolean;
    is_active?: boolean;
  }>({
    filter: functionsFilter,
    fields: 'id,module_id,route,code,name,description,position,is_show,is_active',
    requestKey: `order-functions-${customerId}`,
  });

  if (functions.length === 0) return;

  // Kiểm tra xem customer đã có functions nào chưa (để tránh duplicate khi nâng cấp gói)
  const existingCustomerFunctions = await customerFunctionsCollection().getFullList<{
    id: string;
    customer_id: string;
    function_id: string;
  }>({
    filter: `customer_id="${customerId}"`,
    fields: 'id,customer_id,function_id',
    requestKey: `existing-customer-functions-${customerId}`,
  });

  const existingFunctionIds = new Set(existingCustomerFunctions.map((cf) => cf.function_id));

  // Snapshot functions vào customer_functions
  // Chỉ tạo mới nếu function chưa tồn tại (tránh duplicate khi nâng cấp)
  const functionsToSnapshot = functions.filter((f) => !existingFunctionIds.has(f.id));

  for (const func of functionsToSnapshot) {
    // Sequential calls to avoid PocketBase auto-cancellation
    // eslint-disable-next-line no-await-in-loop
    await customerFunctionsCollection().create({
      customer_id: customerId,
      function_id: func.id,
      module_id: func.module_id || undefined,
      route: func.route || undefined,
      code: func.code,
      name: func.name,
      description: func.description || undefined,
      position: func.position || undefined,
      is_show: func.is_show ?? undefined,
      is_active: func.is_active ?? undefined,
    });
  }
};

const deleteOrderDetails = async (orderId: string) => {
  const details = await orderDetailsCollection().getFullList<OrderDetailModel>({
    filter: `order_id="${orderId}"`,
    fields: 'id',
    requestKey: `order-details-delete-${orderId}`,
  });
  for (const detail of details) {
    // eslint-disable-next-line no-await-in-loop
    await orderDetailsCollection().delete(detail.id);
  }
};

const dispatchCustomerAccountEmail = async (
  payload: CustomerAccountEmailPayload,
  options?: { notifySuccess?: boolean }
) => {
  try {
    const loginUrl =
      typeof window !== 'undefined' && window.location
        ? `${window.location.origin}/login`
        : '/login';
    
    await http.post(MAIL_ENDPOINT, {
      ...payload,
      login_url: loginUrl,
    });
    
    if (options?.notifySuccess) {
      toast.success('Đã gửi email thông tin đăng nhập cho khách hàng');
    }
  } catch (error: unknown) {
    // Log warning thay vì error để không làm gián đoạn flow
    console.warn('Email service not available:', {
      endpoint: MAIL_ENDPOINT,
      error: error instanceof Error ? error.message : 'Unknown error',
      payload: {
        email: payload.email,
        customer_name: payload.customer_name,
        username: payload.username,
        // Không log password
      },
    });
    
    // Chỉ hiển thị toast nếu được yêu cầu
    // Không throw error để không làm gián đoạn quá trình tạo đơn hàng
    if (options?.notifySuccess) {
      toast.error('Không thể gửi email thông tin đăng nhập cho khách hàng. Vui lòng thông báo thủ công.');
    }
    
    // Log thông tin để admin có thể gửi email thủ công
    console.info('Customer account information (send manually if needed):', {
      email: payload.email,
      customer_name: payload.customer_name,
      username: payload.username,
      password: payload.password, // Chỉ log trong development
      login_url: typeof window !== 'undefined' && window.location
        ? `${window.location.origin}/login`
        : '/login',
    });
  }
};

export const orderApi = {
  async list(filters?: OrderFilters): Promise<OrderRecord[]> {
    const page = await ordersCollection().getList<OrderRecordModel>(1, 50, {
      filter: buildFilter(filters),
      sort: '-created_at',
      expand: 'customers_id',
    });
    return page.items.map((item) => mapOrderRecord(item));
  },

  async get(id: string): Promise<OrderRecord> {
    const record = await ordersCollection().getOne<OrderRecordModel>(id, {
      expand: 'customers_id',
    });
    const details = await fetchOrderDetails(id);
    return mapOrderRecord(record, details);
  },

  async create(values: OrderFormValues): Promise<OrderRecord> {
    try {
      const customerId = await ensureCustomer(values);
      const { resolved, totalAmount } = await resolveOrderItems(values.order_items);
      const code = generateOrderCode();
      const status: OrderStatus = values.is_paid ? 'completed' : 'pending';

      const orderRecord = await ordersCollection().create({
        code,
        customers_id: customerId,
        total_amount: totalAmount,
        status,
        notes: values.notes,
      });

      await createOrderDetails(orderRecord.id, resolved);

      if (status === 'completed') {
        const packageIds = Array.from(new Set(resolved.map((item) => item.package_id))).filter(Boolean);
        await Promise.all([
          createSubscriptionPlaceholders(customerId, orderRecord.id, resolved),
          createTransactionPlaceholder(orderRecord.id, totalAmount),
          snapshotCustomerFunctions(customerId, packageIds),
        ]);
      }

      toast.success('Tạo đơn hàng thành công');
      return this.get(orderRecord.id);
    } catch (error) {
      console.error('create order error', error);
      toast.error('Không thể tạo đơn hàng');
      throw error;
    }
  },

  async update(id: string, values: Pick<OrderFormValues, 'order_items' | 'notes'>): Promise<OrderRecord> {
    try {
      const { resolved, totalAmount } = await resolveOrderItems(values.order_items);
      await ordersCollection().update(id, {
        total_amount: totalAmount,
        notes: values.notes,
      });
      await deleteOrderDetails(id);
      await createOrderDetails(id, resolved);
      toast.success('Cập nhật đơn hàng thành công');
      return this.get(id);
    } catch (error) {
      console.error('update order error', error);
      toast.error('Không thể cập nhật đơn hàng');
      throw error;
    }
  },

  async delete(id: string): Promise<OrderRecord> {
    try {
      const record = await ordersCollection().update(id, { status: 'cancelled' });
      toast.success('Đã hủy đơn hàng');
      return mapOrderRecord(record as OrderRecordModel);
    } catch (error) {
      console.error('delete order error', error);
      toast.error('Không thể hủy đơn hàng');
      throw error;
    }
  },

  async markAsPaid(id: string): Promise<OrderRecord> {
    try {
      const order = await this.get(id);
      if (order.status === 'completed') {
        return order;
      }
      if (!order.customers_id) {
        throw new Error('Đơn hàng chưa liên kết khách hàng');
      }
      const details = order.order_details ?? (await fetchOrderDetails(id));
      const packageIds = Array.from(new Set(details.map((detail) => detail.package_id))).filter(Boolean);
      await Promise.all([
        ordersCollection().update(id, { status: 'completed', approved_at: new Date().toISOString() }),
        // Cập nhật customer status thành 'active' khi thanh toán thành công
        customersCollection().update(order.customers_id, { status: 'active' }),
        createSubscriptionPlaceholders(
          order.customers_id,
          id,
          details.map((detail) => ({ package_id: detail.package_id, quantity: detail.quantity }))
        ),
        createTransactionPlaceholder(id, order.total_amount),
        snapshotCustomerFunctions(order.customers_id, packageIds),
      ]);
      toast.success('Đã đánh dấu thanh toán đơn hàng');
      return this.get(id);
    } catch (error) {
      console.error('markAsPaid error', error);
      toast.error('Không thể đánh dấu thanh toán');
      throw error;
    }
  },

  async getCustomers(search?: string): Promise<CustomerOption[]> {
    const clauses = ['is_deleted!=true'];
    if (search) {
      const keyword = search.replace(/"/g, '\\"');
      clauses.push(`(name~"${keyword}"||email~"${keyword}"||tax_code~"${keyword}")`);
    }
    const filter = clauses.join('&&');
    const records = await customersCollection().getList<CustomerRecordModel>(1, 20, {
      filter,
      sort: '-created_at',
      requestKey: `order-customers-${search ?? 'all'}`,
    });
    return records.items.map((item) => ({
      id: item.id,
      name: item.name,
      email: item.email ?? undefined,
      representative_name: item.representative_name ?? undefined,
      status: (item.status ?? 'active') as 'active' | 'inactive',
      subscriptions_count: item.subscriptions_count,
    }));
  },

  async getPackages(): Promise<PackageOption[]> {
    const records = await packagesCollection().getFullList<PackageRecordModel>({
      filter: 'is_deleted!=true&&status="active"',
      sort: 'position',
      fields: 'id,name,code,sale_price',
      requestKey: 'order-packages',
    });
    return records.map((pkg) => ({
      id: pkg.id,
      name: pkg.name,
      code: pkg.code,
      sale_price: Number(pkg.sale_price ?? 0),
    }));
  },
  async sendCustomerAccountEmail(payload: CustomerAccountEmailPayload) {
    await dispatchCustomerAccountEmail(payload, { notifySuccess: true });
  },

  async getCustomerDetail(id: string): Promise<CustomerOption> {
    const record = await customersCollection().getOne<CustomerRecordModel>(id);
    return {
      id: record.id,
      name: record.name,
      email: record.email ?? undefined,
      representative_name: record.representative_name ?? undefined,
      representative_phone: record.representative_phone ?? undefined,
      phone: record.phone ?? undefined,
      address: record.address ?? undefined,
      tax_code: record.tax_code ?? undefined,
      customer_type_id: record.customer_type_id ?? undefined,
      source: record.source ?? undefined,
      status: (record.status ?? 'active') as 'active' | 'inactive',
      subscriptions_count: record.subscriptions_count,
    };
  },

  async getTodayOrders(): Promise<OrderRecord[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const filter = `created_at>="${today.toISOString()}"&&created_at<"${tomorrow.toISOString()}"&&status!="deleted"`;
    const page = await ordersCollection().getList<OrderRecordModel>(1, 1000, {
      filter,
      sort: '-created_at',
      expand: 'customers_id',
    });
    return page.items.map((item) => mapOrderRecord(item));
  },

  async getPendingOrders(): Promise<OrderRecord[]> {
    const filter = `status="pending"&&status!="deleted"`;
    const page = await ordersCollection().getList<OrderRecordModel>(1, 1000, {
      filter,
      sort: '-created_at',
      expand: 'customers_id',
    });
    return page.items.map((item) => mapOrderRecord(item));
  },

  async getTodayRevenue(): Promise<number> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const filter = `status="completed"&&created_at>="${today.toISOString()}"&&created_at<"${tomorrow.toISOString()}"&&status!="deleted"`;
    const page = await ordersCollection().getList<OrderRecordModel>(1, 1000, {
      filter,
      fields: 'total_amount',
    });

    return page.items.reduce((sum, item) => sum + Number(item.total_amount ?? 0), 0);
  },

  async getOrdersStatistics(dateFrom?: string, dateTo?: string): Promise<{
    ordersByDate: Array<{ date: string; count: number; revenue: number }>;
    statusDistribution: Array<{ status: OrderStatus; count: number }>;
  }> {
    const clauses: string[] = ['status!="deleted"'];
    
    if (dateFrom) {
      const start = new Date(dateFrom);
      start.setHours(0, 0, 0, 0);
      clauses.push(`created_at>="${start.toISOString()}"`);
    }
    
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      clauses.push(`created_at<="${end.toISOString()}"`);
    }

    const filter = clauses.join('&&');
    const page = await ordersCollection().getList<OrderRecordModel>(1, 1000, {
      filter,
      sort: 'created_at',
      fields: 'id,status,total_amount,created_at',
    });

    // Group by date
    const ordersByDateMap = new Map<string, { count: number; revenue: number }>();
    const statusMap = new Map<OrderStatus, number>();

    page.items.forEach((item) => {
      const status = (item.status ?? 'pending') as OrderStatus;
      const amount = Number(item.total_amount ?? 0);
      const createdAt = item.created_at ?? item.created;

      // Group by date
      if (createdAt) {
        const date = new Date(createdAt);
        const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        const existing = ordersByDateMap.get(dateKey) || { count: 0, revenue: 0 };
        ordersByDateMap.set(dateKey, {
          count: existing.count + 1,
          revenue: existing.revenue + (status === 'completed' ? amount : 0),
        });
      }

      // Count by status
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    });

    // Convert to arrays
    const ordersByDate = Array.from(ordersByDateMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const statusDistribution = Array.from(statusMap.entries()).map(([status, count]) => ({
      status,
      count,
    }));

    return {
      ordersByDate,
      statusDistribution,
    };
  },
};


